// Task dispatch (#1034) — `POST /v1/tasks`, the ONE dispatch path (behind the
// domain `Dispatcher` port). `lib/tasks.ts` keeps the read half.
//
// **A 2xx is not "dispatched."** The route answers the dispatcher's own verdict
// (201 dispatched, 201 QUEUED, 202 ESCALATED, 200 DUPE) and `readOutcome` reads
// it off the body's own discriminators — the rule the forced issue actions hold
// too (`lib/issue-actions.ts`). Every refusal stays ONE outcome carrying status,
// `detail` and extension members; `lib/task-refusal.ts` alone reads them as copy.

import type { TaskSink } from '@developerz/domain';
import { ApiError, apiFetch } from './api';
import { SESSION_TOKEN } from './session';

/** What the short form collects; the rest of the envelope is defaulted below. */
export type NewTaskInput = {
  readonly repo_full_name: string;
  readonly title: string;
  readonly problem: string;
  readonly acceptance_criteria: readonly string[];
  readonly sink: TaskSink;
};

/** The dispatcher's verdict, as the REST surface spells it. */
export type DispatchOutcome =
  | { readonly kind: 'dispatched'; readonly task_id: string; readonly url: string | null }
  | {
      readonly kind: 'queued';
      readonly task_id: string;
      readonly queue_position: number;
      readonly queue_depth: number;
    }
  | { readonly kind: 'escalated'; readonly confidence: number; readonly threshold: number }
  | { readonly kind: 'dupe'; readonly dupe_of_task_id: string; readonly linked_pr: number | null }
  /** A 2xx that named neither a task nor a verdict — see `readOutcome`. */
  | { readonly kind: 'unclear' }
  /** Any non-2xx: `status` picks the copy, `detail` + `extensions` are the reason. */
  | {
      readonly kind: 'refused';
      readonly status: number;
      readonly detail: string | null;
      readonly extensions?: Readonly<Record<string, unknown>>;
    };

/** `TaskEnvelopeSchema` caps `acceptance_criteria` at 20 entries. */
export const ACCEPTANCE_CRITERIA_MAX = 20;

/**
 * The sinks the picker offers — capacity-aware first (the default). `direct` is
 * deliberately absent: it clears the SAME capability as `queued`
 * (`Capability.HandoffWebhook`, `packages/agent/src/dispatch/task-dispatcher.ts`)
 * and differs only in that `queued` enforces the org's max queue depth, so
 * offering both would put a one-click bypass of a billing entitlement one line
 * above the 429 announcing it. `linear` is absent because no deployment wires
 * that sink (`apps/api/src/deps/army.ts`) — it could only ever 501.
 */
export const SINK_ORDER: readonly TaskSink[] = ['queued', 'gh'];

/** Label key per sink — the picker offers two; the detail surface renders all four. */
export const SINK_LABEL: Record<TaskSink, string> = {
  queued: 'fleet.new_task.sink_queued',
  direct: 'fleet.new_task.sink_direct',
  gh: 'fleet.new_task.sink_gh',
  linear: 'fleet.new_task.sink_linear',
};

/** One acceptance criterion per line — blanks dropped, capped at the schema's 20. */
export function toCriteria(text: string): string[] {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .slice(0, ACCEPTANCE_CRITERIA_MAX);
}

/**
 * The verdict a 2xx body carries, read off its own discriminators: `escalated`
 * and `dupe` are explicit flags, a `queue_position` marks the capacity-aware
 * enqueue, and a `task_id` IS the dispatch. Dispatch is NOT the default branch —
 * a 204 (`apiFetch` yields `undefined`), a proxy-injected 200 and a verdict
 * added server-side later name no task, so they answer `unclear` rather than
 * claim work the fleet may never have been handed.
 */
export function readOutcome(body: Record<string, unknown>): DispatchOutcome {
  const num = (v: unknown): number => (typeof v === 'number' ? v : 0);
  const str = (v: unknown): string => String(v ?? '');
  if (body['escalated'] === true) {
    return {
      kind: 'escalated',
      confidence: num(body['confidence']),
      threshold: num(body['threshold']),
    };
  }
  if (body['dupe'] === true) {
    return {
      kind: 'dupe',
      dupe_of_task_id: str(body['dupe_of_task_id']),
      linked_pr: typeof body['linked_pr'] === 'number' ? body['linked_pr'] : null,
    };
  }
  if (typeof body['queue_position'] === 'number') {
    return {
      kind: 'queued',
      task_id: str(body['task_id']),
      queue_position: body['queue_position'],
      queue_depth: num(body['queue_depth']),
    };
  }
  if (typeof body['task_id'] === 'string') {
    return {
      kind: 'dispatched',
      task_id: body['task_id'],
      url: typeof body['url'] === 'string' ? body['url'] : null,
    };
  }
  return { kind: 'unclear' };
}

/**
 * Dispatch one task. The envelope fields the short form doesn't collect are sent
 * as the neutral defaults the schema requires: `files_touched` / `forbidden`
 * empty (advisory hints a scoped plan supplies), and `confidence: 1` — what
 * every system producer stamps for work a human, not a planner, scoped.
 * `origin` is omitted so the route's `manual` default stands. Never throws: a
 * refusal IS an outcome, and a 401 already latched the session-expiry gate.
 */
export async function dispatchTask(
  input: NewTaskInput,
  token: string | null = SESSION_TOKEN,
): Promise<DispatchOutcome> {
  try {
    const body = await apiFetch<Record<string, unknown>>('/v1/tasks', token, {
      method: 'POST',
      body: JSON.stringify({ ...input, files_touched: [], forbidden: [], confidence: 1 }),
    });
    return readOutcome(body ?? {});
  } catch (err) {
    if (err instanceof ApiError) {
      return {
        kind: 'refused',
        status: err.status,
        detail: err.detail ?? null,
        // Absent when the body carried none — never an empty bag.
        ...(err.extensions === undefined ? {} : { extensions: err.extensions }),
      };
    }
    // A dead network has no status; `refusalCopy` frames a 0 generically.
    return { kind: 'refused', status: 0, detail: null };
  }
}
